import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Mail, RefreshCw, CheckCircle, ArrowLeft } from "lucide-react";

interface EmailVerificationPendingProps {
  email: string;
  onBackToLogin: () => void;
}

export function EmailVerificationPending({ email, onBackToLogin }: EmailVerificationPendingProps) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const { toast } = useToast();

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      setResent(true);
      toast({
        title: "Email sent! 📧",
        description: "Check your inbox for the verification link.",
      });

      // Reset resent state after 60 seconds
      setTimeout(() => setResent(false), 60000);
    } catch (error: any) {
      toast({
        title: "Failed to resend",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <Card className="w-full max-w-md p-8 text-center">
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-primary/10 rounded-full">
            <Mail className="h-12 w-12 text-primary" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-2">
          Check your email! 📬
        </h1>
        
        <p className="text-muted-foreground mb-6">
          We've sent a verification link to:
        </p>
        
        <p className="font-medium text-foreground bg-muted px-4 py-2 rounded-md mb-6 break-all">
          {email}
        </p>

        <p className="text-sm text-muted-foreground mb-8">
          Click the link in the email to verify your account and start saving money on taxes! 🚀
        </p>

        <div className="space-y-3">
          <Button
            onClick={handleResendEmail}
            disabled={resending || resent}
            variant="outline"
            className="w-full"
          >
            {resending ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : resent ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                Email sent! Check inbox
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Resend verification email
              </>
            )}
          </Button>

          <Button
            onClick={onBackToLogin}
            variant="ghost"
            className="w-full"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to login
          </Button>
        </div>

        <div className="mt-8 pt-6 border-t">
          <p className="text-xs text-muted-foreground">
            <strong>Didn't receive the email?</strong>
            <br />
            Check your spam folder, or try resending after a minute.
          </p>
        </div>
      </Card>
    </div>
  );
}
